/**
 * The phone's side of live traffic: sample its own speed per cell, send it
 * late and anonymously, draw what the network knows, and add the delay to the
 * ETA. See shared/traffic.ts for the privacy design.
 */
import type maplibregl from "maplibre-gl";
import type { Feature, FeatureCollection } from "geojson";
import type { Fix } from "../../shared/alerts.js";
import { decodeGeohash } from "../../shared/geohash.js";
import { cellOf, delayFor, octantOf, SLOW_KMH, STOPPED_KMH, type TrafficItem, type TrafficSample } from "../../shared/traffic.js";
import type { RouteIndex } from "../../shared/route.js";

/** gh7 cell size, roughly: 153 m × 153 m at the equator; ~115 m E–W in Albania. */
const GH7_LAT = 0.001373, GH7_LON = 0.001373;

export class TrafficSampler {
  private current: { key: string; g: string; o: number; sum: number; n: number; since: number } | null = null;
  private queue: TrafficSample[] = [];
  private timer: number | null = null;

  constructor(private readonly apiBase: string) {}

  /** Per fix. Stopped in one cell for over two minutes = parked, not traffic. */
  sample(fix: Fix): void {
    if (fix.course === null) return;
    const g = cellOf(fix.lat, fix.lon);
    const o = octantOf(fix.course);
    const key = `${g}:${o}`;
    const kmh = fix.speedMps * 3.6;
    if (this.current === null || this.current.key !== key) {
      this.finish();
      this.current = { key, g, o, sum: 0, n: 0, since: fix.at };
    }
    const c = this.current;
    if (kmh < 2 && fix.at - c.since > 120_000) return;
    c.sum += kmh;
    c.n += 1;
  }

  private finish(): void {
    const c = this.current;
    this.current = null;
    if (c === null || c.n < 2) return;
    this.queue.push({ g: c.g, o: c.o, k: Math.round((c.sum / c.n) * 10) / 10, n: c.n });
    this.schedule();
  }

  /** Random 30–90 s delay before a batch leaves, so a sample cannot be matched
   *  to the moment a car passed. */
  private schedule(): void {
    if (this.timer !== null) return;
    this.timer = window.setTimeout(() => { this.timer = null; void this.flush(); }, 30_000 + Math.random() * 60_000);
  }

  async flush(): Promise<void> {
    if (this.queue.length === 0) return;
    const batch = this.queue.splice(0, 40);
    try {
      await fetch(`${this.apiBase}/v1/traffic`, {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(batch), keepalive: true,
      });
    } catch {
      // Lost samples are lost; traffic is a live signal, not a ledger.
    }
    if (this.queue.length > 0) this.schedule();
  }

  /** End of drive: close the open cell and send what is left. */
  end(): void {
    this.finish();
    void this.flush();
  }
}

/* ---------------- drawing ---------------- */

/** Slow cells as a translucent haze on the map. Two greys: slow and stopped. */
export function trafficGeoJson(items: Iterable<TrafficItem>): FeatureCollection {
  const seen = new Map<string, number>();
  for (const it of items) {
    if (it.k >= SLOW_KMH) continue;
    // Both octants of a cell could be slow; keep the slowest for the fill.
    const prev = seen.get(it.g);
    if (prev === undefined || it.k < prev) seen.set(it.g, it.k);
  }
  const features: Feature[] = [];
  for (const [g, k] of seen) {
    const c = decodeGeohash(g);
    const lat0 = c.lat - GH7_LAT / 2, lat1 = c.lat + GH7_LAT / 2, lon0 = c.lon - GH7_LON / 2, lon1 = c.lon + GH7_LON / 2;
    features.push({
      type: "Feature", properties: { stopped: k < STOPPED_KMH ? 1 : 0 },
      geometry: { type: "Polygon", coordinates: [[[lon0, lat0], [lon1, lat0], [lon1, lat1], [lon0, lat1], [lon0, lat0]]] },
    });
  }
  return { type: "FeatureCollection", features };
}

export function installTrafficLayers(map: maplibregl.Map): void {
  map.addSource("traffic", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
  map.addLayer({
    id: "traffic-fill", type: "fill", source: "traffic",
    paint: { "fill-color": "#FFFFFF", "fill-opacity": ["case", ["==", ["get", "stopped"], 1], 0.26, 0.14] },
  });
}

/* ---------------- ETA ---------------- */

/** Seconds of delay the fresh traffic adds along the route ahead of `alongM`. */
export function routeDelayS(idx: RouteIndex, alongM: number, traffic: Map<string, TrafficItem>): number {
  if (traffic.size === 0) return 0;
  const g = idx.route.geometry;
  const avgKmh = (idx.route.distanceM / Math.max(1, idx.route.durationS)) * 3.6;
  let delay = 0;
  for (let i = 0; i < g.length - 1; i++) {
    const segEnd = idx.cum[i + 1] as number;
    if (segEnd < alongM) continue;
    const a = g[i] as readonly [number, number];
    const b = g[i + 1] as readonly [number, number];
    const len = segEnd - (idx.cum[i] as number);
    const y = Math.sin(((b[1] - a[1]) * Math.PI) / 180) * Math.cos((b[0] * Math.PI) / 180);
    const x = Math.cos((a[0] * Math.PI) / 180) * Math.sin((b[0] * Math.PI) / 180) -
      Math.sin((a[0] * Math.PI) / 180) * Math.cos((b[0] * Math.PI) / 180) * Math.cos(((b[1] - a[1]) * Math.PI) / 180);
    const bearing = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
    const it = traffic.get(`${cellOf((a[0] + b[0]) / 2, (a[1] + b[1]) / 2)}:${octantOf(bearing)}`);
    if (it === undefined) continue;
    delay += delayFor(len, it.k, avgKmh);
  }
  return delay;
}
